import React, { Component } from 'react';
import ErrorEventEmitter, { ERROR_EVENTS, ErrorEventData } from 'services/ErrorEventEmitter';
import styled, { keyframes } from 'styled-components';

const slideIn = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const colors = {
  errorBg: '#FFEEF0',
  errorBorder: '#F48FB1',
  errorText: '#D32F2F',
  errorHeaderBg: '#FFEBEE',
  buttonPrimary: '#F50057',
  buttonPrimaryHover: '#C51162',
  iconHover: '#F50057',
};

const ErrorOverlay = styled.div<{ minimized: boolean }>`
  position: fixed;
  bottom: 24px;
  left: 24px;
  width: 420px;
  max-height: ${(props) => (props.minimized ? '60px' : '85vh')};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: white;
  border: 1px solid ${colors.errorBorder};
  border-radius: 8px;
  z-index: 9999;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  animation: ${slideIn} 0.3s ease-out;
  transition: all 0.3s ease;
`;

const ErrorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: ${colors.errorHeaderBg};
  border-bottom: 1px solid ${colors.errorBorder};
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ErrorTitle = styled.h4`
  margin: 0;
  color: ${colors.errorText};
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;

  &::before {
    content: '⚠️';
    margin-right: 8px;
    font-size: 18px;
  }
`;

const MinimizeButton = styled.button`
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  margin-right: 4px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: ${colors.iconHover};
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 20px;
  padding: 4px;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: ${colors.iconHover};
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

const ErrorContent = styled.div`
  padding: 16px;
  overflow-y: auto;
  flex: 1;
`;

const ErrorMessage = styled.div`
  margin-bottom: 12px;
  font-weight: 500;
  font-size: 14px;
  color: ${colors.errorText};
  line-height: 1.5;
  padding: 12px;
  background-color: ${colors.errorBg};
  border-radius: 4px;
  border-left: 4px solid ${colors.errorText};
`;

const ErrorStackLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #666;
`;

const ErrorStack = styled.pre`
  margin: 0 0 16px 0;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 250px;
  overflow: auto;
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  font-family: 'SF Mono', SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 12px;
  color: #333;
  border: 1px solid #e2e8f0;
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px;
  background-color: #fafafa;
  border-top: 1px solid #edf2f7;
`;

const Button = styled.button`
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.5);
  }
`;

const ReloadButton = styled(Button)`
  background-color: ${colors.buttonPrimary};
  color: white;
  margin-left: 12px;

  &:hover {
    background-color: ${colors.buttonPrimaryHover};
  }

  &:active {
    transform: translateY(1px);
  }

  &::before {
    content: '🔄';
    margin-right: 8px;
    font-size: 14px;
  }
`;

interface ErrorOverlayManagerState extends ErrorEventData {
  visible: boolean;
  minimized: boolean;
}

export class ErrorOverlayManager extends Component<{}, ErrorOverlayManagerState> {
  state = {
    error: null,
    errorInfo: null,
    visible: false,
    minimized: false,
  };

  private unsubscribeError: (() => void) | null = null;
  private unsubscribeDismiss: (() => void) | null = null;

  componentDidMount() {
    this.unsubscribeError = ErrorEventEmitter.on(ERROR_EVENTS.ERROR_OCCURRED, this.handleErrorEvent);
    this.unsubscribeDismiss = ErrorEventEmitter.on(ERROR_EVENTS.ERROR_DISMISSED, this.handleErrorDismissed);
  }

  componentWillUnmount() {
    if (this.unsubscribeError) {
      this.unsubscribeError();
    }

    if (this.unsubscribeDismiss) {
      this.unsubscribeDismiss();
    }
  }

  private handleErrorEvent = (data: ErrorEventData) => {
    if (data && data.error) {
      this.setState({
        error: data.error,
        errorInfo: data.errorInfo,
        visible: true,
      });
    }
  };

  private handleErrorDismissed = () => {
    this.setState({ visible: false });
  };

  private closeErrorOverlay = () => {
    this.setState({ visible: false });
  };

  private toggleMinimized = () => {
    this.setState((prevState) => ({ minimized: !prevState.minimized }));
  };

  private reloadPage = () => {
    window.location.reload();
  };

  render() {
    const { error, errorInfo, visible, minimized } = this.state;

    if (!visible || !error) {
      return null;
    }

    const errorName = error ? (error as any).name : 'Unknown Error';
    const errorMessage = error ? (error as any).message : '';
    const componentStack = errorInfo && (errorInfo as any).componentStack ? (errorInfo as any).componentStack : '';

    return (
      <ErrorOverlay minimized={minimized}>
        <ErrorHeader>
          <ErrorTitle>{errorName}</ErrorTitle>
          <ButtonContainer>
            <MinimizeButton onClick={this.toggleMinimized} aria-label={this.state.minimized ? 'Expand' : 'Minimize'}>
              {this.state.minimized ? '▲' : '▼'}
            </MinimizeButton>
            <CloseButton onClick={this.closeErrorOverlay} aria-label="Close">
              ×
            </CloseButton>
          </ButtonContainer>
        </ErrorHeader>
        {!minimized && (
          <ErrorContent>
            <ErrorMessage>{errorMessage}</ErrorMessage>
            {componentStack && (
              <>
                <ErrorStackLabel>Stack trace</ErrorStackLabel>
                <ErrorStack>{componentStack}</ErrorStack>
              </>
            )}
          </ErrorContent>
        )}
        {!minimized && (
          <ButtonGroup>
            <ReloadButton onClick={this.reloadPage}>Reload</ReloadButton>
          </ButtonGroup>
        )}
      </ErrorOverlay>
    );
  }
}

export default ErrorOverlayManager;
